import {
  ParsedReportParams,
  ReportBackendTypes,
  useGetShareGroups,
} from '@ais-ui/websockets';
import { ReportType } from '../../../reportsTypes';
import { useCallback, useMemo } from 'react';
import { getFrontendColorScaleFromBackend } from '../../utils/densityColorMapping';
import { parseCustomDuration } from '../../../../utils/formatDuration';
import {
  parseCsvMiscellaneousDataFields,
  parseCsvMiscellaneousMessageTypes,
} from '../../utils/parseCsvMiscellaneous';
import { convertToMinCrossingInterval } from '../../utils/convertToMinCrossingInterval';
import { parseCoverageMiscellaneousPlotType } from '../../utils/parseCoverageMiscellaneous';
import { convertBackToColorIntervals } from '../../utils/parseColorInterval';
import {
  useConditionalEventdetectorDefinitionsFetch,
  useConditionalWatchdogDefinitionsFetch,
} from '@ais-ui/api';
import { SelectType } from '@ais-ui/ui-core';

export function useInitializeMiscellaneous(
  report: ParsedReportParams | undefined,
  reportType: ReportType
) {
  const { data: groups, isLoading } = useGetShareGroups();
  const {
    data: watchdogDefinitions,
    isLoading: isLoadingWatchdogs,
    isError: isErrorWatchdogs,
  } = useConditionalWatchdogDefinitionsFetch(
    reportType.type === ReportBackendTypes.WATCHDOG_EVENTS
  );

  const {
    data: eventDefinitions,
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
  } = useConditionalEventdetectorDefinitionsFetch(
    reportType.type === ReportBackendTypes.WATCHDOG_EVENTS
  );

  const matchingWatchdogAndEventDefinitions = useMemo(() => {
    if (!watchdogDefinitions?.records || !eventDefinitions?.records) {
      return [];
    }

    const watchdogAndEventIds = report?.params['WD'] || [];

    const watchdogMapped = watchdogDefinitions.records
      .filter((record) => watchdogAndEventIds.includes(record.id))
      .map((record) => ({
        label: record.name,
        value: record.id,
        type: 'watchdog', // Optional to distinguish source
      }));

    const eventMapped = eventDefinitions.records
      .filter((record) => watchdogAndEventIds.includes(record.id))
      .map((record) => ({
        label: record.name,
        value: record.id,
        type: 'event', // Optional to distinguish source
      }));

    return [...watchdogMapped, ...eventMapped];
  }, [watchdogDefinitions, eventDefinitions, report]);

  return useCallback(() => {
    const notifyDefaults = {
      email: '',
      countryCode: '',
      phone: '',
    };

    const shareDefaults = {
      selectedGroups:
        !isLoading && groups && report?.params['SR']
          ? groups
              .filter((group) => report.params['SR'].includes(group.uuid))
              .map((group) => ({
                value: group.uuid,
                label: group.name,
              }))
          : [],
    };

    switch (reportType.type) {
      case ReportBackendTypes.AREA:
        return {
          statisticGrouping: report?.params['STG'],
          interval: report?.params['PI'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.DENSITY:
        return {
          colorScale: getFrontendColorScaleFromBackend(report?.params['CS']),
          colorBy: report?.params['CB'],
          cellsPerDegree: report?.params['CPD']
            ? report?.params['CPD'].toString()
            : undefined,
          colorDivision: report?.params['CD'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.TRIPS:
        return {
          maxDuration: parseCustomDuration(report?.params['MDUR']),
          excludeLocalOperations: Boolean(report?.params['EL']),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP:
        return {
          maxClosestPointOfApproachDistance: report?.params['MCD'],
          minDeltaCourseOverGround: JSON.parse(report?.params['NMSSAS'])[
            'min_dcog'
          ],
          maxDeltaCourseOverGround: JSON.parse(report?.params['NMSSAS'])[
            'max_dcog'
          ],
          minDeltaSpeedOverGround: JSON.parse(report?.params['NMSSAS'])[
            'min_dsog'
          ],
          maxDeltaSpeedOverGround: JSON.parse(report?.params['NMSSAS'])[
            'max_dsog'
          ],
          minSpeedOverGround: JSON.parse(report?.params['NMSSAS'])['min_sog'],
          maxSpeedOverGround: JSON.parse(report?.params['NMSSAS'])['max_sog'],
          minDuration: JSON.parse(report?.params['NMSSAS'])['min_dur'],
          maxDuration: JSON.parse(report?.params['NMSSAS'])['max_dur'],
          minTravelDistance: JSON.parse(report?.params['NMSSAS'])['min_dist'],
          maxTravelDistance: JSON.parse(report?.params['NMSSAS'])['max_dist'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT:
        return {
          distanceThreshold: report?.params['DT'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.PASSAGELINE:
        return {
          outputType: report?.params['PAC'],
          statisticGrouping: report?.params['STG'],
          interval: report?.params['PI'],
          minCrossingInterval: convertToMinCrossingInterval(
            report?.params['MCI']
          ),
          minCrossingDistance: report?.params['MCPAD'],
          columnDivisionValue: report?.params['DCDV'],
          columnDivisionUnit: report?.params['DCDU'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.PASSAGELINE_SHIPS:
        return {
          maxDuration: parseCustomDuration(report?.params['MDUR']),
          outputType: report?.params['PAC'],
          statisticGrouping: report?.params['STG'],
          interval: report?.params['PI'],
          bothDirections: report?.params['BDIR'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.TRANSPONDER_OFF:
        return {
          maxDuration: parseCustomDuration(report?.params['MDUR']),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.SOG_DISTRIBUTION:
        return {
          distribution: report?.params['STG'],
          interval: report?.params['PI'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.FISHING:
        return {
          minDuration: parseCustomDuration(report?.params['MIDUR']),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.MULTI_SHAPE:
        return {
          maxDuration: parseCustomDuration(report?.params['MDUR']),
          outputType: report?.params['PAC'],
          statisticGrouping: report?.params['STG'],
          interval: report?.params['PI'],
          bothDirections: report?.params['BDIR'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.PILOT_OPERATIONS:
        return {
          maxClosestPointOfApproachDistance: JSON.parse(
            report?.params['SHPOP']
          )['md'],
          minDeltaCourseOverGround: JSON.parse(report?.params['SHPOP'])[
            'min_dcog'
          ],
          maxDeltaCourseOverGround: JSON.parse(report?.params['SHPOP'])[
            'max_dcog'
          ],
          minDeltaSpeedOverGround: JSON.parse(report?.params['SHPOP'])[
            'min_dsog'
          ],
          maxDeltaSpeedOverGround: JSON.parse(report?.params['SHPOP'])[
            'max_dsog'
          ],
          minSpeedOverGround: JSON.parse(report?.params['SHPOP'])['min_sog'],
          maxSpeedOverGround: JSON.parse(report?.params['SHPOP'])['max_sog'],
          minDuration: JSON.parse(report?.params['SHPOP'])['min_dur'],
          maxDuration: JSON.parse(report?.params['SHPOP'])['max_dur'],
          minTravelDistance: JSON.parse(report?.params['SHPOP'])['min_dist'],
          maxTravelDistance: JSON.parse(report?.params['SHPOP'])['max_dist'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.TRANSIT_PILOTAGE:
        return {
          maxClosestPointOfApproachDistance: JSON.parse(
            report?.params['SHPOP']
          )['md'],
          minDeltaCourseOverGround: JSON.parse(report?.params['SHPOP'])[
            'min_dcog'
          ],
          maxDeltaCourseOverGround: JSON.parse(report?.params['SHPOP'])[
            'max_dcog'
          ],
          minDeltaSpeedOverGround: JSON.parse(report?.params['SHPOP'])[
            'min_dsog'
          ],
          maxDeltaSpeedOverGround: JSON.parse(report?.params['SHPOP'])[
            'max_dsog'
          ],
          minSpeedOverGround: JSON.parse(report?.params['SHPOP'])['min_sog'],
          maxSpeedOverGround: JSON.parse(report?.params['SHPOP'])['max_sog'],
          minDuration: JSON.parse(report?.params['SHPOP'])['min_dur'],
          maxDuration: JSON.parse(report?.params['SHPOP'])['max_dur'],
          minTravelDistance: JSON.parse(report?.params['SHPOP'])['min_dist'],
          maxTravelDistance: JSON.parse(report?.params['SHPOP'])['max_dist'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.COVERAGE:
        return {
          cellResolution: report?.params['CR'],
          colorDivision: report?.params['CD'],
          plotType: parseCoverageMiscellaneousPlotType(report?.params['CPTYP']),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.CSV_DATA_EXPORT:
        return {
          dataFields: parseCsvMiscellaneousDataFields(report?.params['CSVP']),
          messageType: parseCsvMiscellaneousMessageTypes(
            report?.params['MSGT']
          ),
          fewFiles: Boolean(report?.params['MF']),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.TRACK:
        return {
          colorBy: report?.params['CB'],
          colorByInterval: convertBackToColorIntervals(
            JSON.parse(report?.params['CBCD'])
          ),
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.WATCHDOG_EVENTS:
        return {
          watchdogDefinitions: matchingWatchdogAndEventDefinitions,
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.SQL:
        return {
          query: report?.params['SQL'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      case ReportBackendTypes.ATON_DETAILED:
        return {
          mmsi: report?.params['MMSI'],
          ...notifyDefaults,
          ...shareDefaults,
        };
      default:
        return { ...notifyDefaults, ...shareDefaults };
    }
  }, [
    isLoading,
    groups,
    report,
    reportType.type,
    matchingWatchdogAndEventDefinitions,
  ]);
}
